import api from './api';
import { Reservation } from './types';

/**
 * Reservations Service
 * Handles all reservation-related API requests
 */
const reservationsService = {
    /**
     * Get all reservations
     * @returns Promise<Reservation[]>
     */
    getAll: async (): Promise<Reservation[]> => {
        try {
            const response = await api.get<{ reservations: Reservation[]; total: number; success: boolean }>('/reservations');
            return response.reservations || [];
        } catch (error) {
            console.error('reservationsService.getAll - Error fetching all reservations:', error);
            throw error;
        }
    },

    /**
     * Get reservation by public ID
     * @param publicId - Reservation public ID
     * @returns Promise<Reservation>
     */
    getById: async (publicId: string): Promise<Reservation> => {
        try {
            const response = await api.get<Reservation>(`/reservations/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching reservation ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get reservations by house ID
     * @param housePublicId - House public ID
     * @returns Promise<Reservation[]>
     */
    getByHouseId: async (housePublicId: string): Promise<Reservation[]> => {
        try {
            const response = await api.get<{ reservations: Reservation[]; total: number; success: boolean }>(`/reservations/house/${housePublicId}`);
            return response.reservations || [];
        } catch (error) {
            console.error(`Error fetching reservations for house ${housePublicId}:`, error);
            throw error;
        }
    },

    /**
     * Get reservations by state
     * @param state - Reservation state (pending, confirmed, cancelled)
     * @returns Promise<Reservation[]>
     */
    getByState: async (state: string): Promise<Reservation[]> => {
        try {
            const response = await api.get<{ reservations: Reservation[]; total: number; success: boolean }>(`/reservations/state/${state}`);
            return response.reservations || [];
        } catch (error) {
            console.error(`Error fetching reservations by state ${state}:`, error);
            throw error;
        }
    },

    /**
     * Get reservations for photos page (filtered by checkout date ±6 months)
     * @returns Promise<Reservation[]>
     */
    getForPhotos: async (): Promise<Reservation[]> => {
        try {
            const response = await api.get<{ reservations: Reservation[]; total: number; success: boolean }>('/reservations/photos');
            return response.reservations || [];
        } catch (error) {
            console.error('reservationsService.getForPhotos - Error fetching reservations for photos:', error);
            throw error;
        }
    },

    /**
     * Create new reservation
     * @param reservationData - Reservation data
     * @returns Promise<Reservation>
     */
    create: async (reservationData: Partial<Reservation>): Promise<Reservation> => {
        try {
            console.log('Creating reservation with user_id:', reservationData.user_id);

            // Transform frontend field names to backend field names
            const backendPayload: any = {
                house_public_id: reservationData.house_id,
                user_public_id: reservationData.user_id || null,
                id_reservation_platform: reservationData.id_reservation_platform || null,
                platform: reservationData.platform || null,
                name_first_guess: reservationData.name_first_guess || null,
                nationality: reservationData.nationality || null,
                contact: reservationData.contact,
                baby: reservationData.baby || false,
                rooms: reservationData.rooms,
                check_in_date: reservationData.check_in_date,
                check_out_date: reservationData.check_out_date,
                check_in_hour: reservationData.check_in_hour || null,
                check_out_hour: reservationData.check_out_hour || null,
                pets: reservationData.pets || false,
                deposit_number: reservationData.deposit_number || null,
                reservation_state: reservationData.reservation_state || 'pending',
                observations: reservationData.observations || null,
                number_of_guests: reservationData.number_of_guests || null,
                is_paid: reservationData.is_paid || false,
                google_calendar_event_id: reservationData.google_calendar_event_id || null,
            };

            console.log('Backend payload:', backendPayload);

            const response = await api.post<Reservation>('/reservations', backendPayload);
            return response;
        } catch (error) {
            console.error('Error creating reservation:', error);
            throw error;
        }
    },

    /**
     * Update reservation
     * @param publicId - Reservation public ID
     * @param reservationData - Reservation data to update
     * @returns Promise<Reservation>
     */
    update: async (publicId: string, reservationData: Partial<Reservation>): Promise<Reservation> => {
        try {
            // Transform frontend field names to backend field names
            const backendPayload: any = {
                house_public_id: reservationData.house_id,
                user_public_id: reservationData.user_id || null,
                id_reservation_platform: reservationData.id_reservation_platform || null,
                platform: reservationData.platform || null,
                name_first_guess: reservationData.name_first_guess || null,
                nationality: reservationData.nationality || null,
                contact: reservationData.contact,
                baby: reservationData.baby || false,
                rooms: reservationData.rooms,
                check_in_date: reservationData.check_in_date,
                check_out_date: reservationData.check_out_date,
                check_in_hour: reservationData.check_in_hour || null,
                check_out_hour: reservationData.check_out_hour || null,
                pets: reservationData.pets || false,
                deposit_number: reservationData.deposit_number || null,
                reservation_state: reservationData.reservation_state || 'pending',
                observations: reservationData.observations || null,
                number_of_guests: reservationData.number_of_guests || null,
                is_paid: reservationData.is_paid || false,
                google_calendar_event_id: reservationData.google_calendar_event_id || null,
            };

            const response = await api.put<Reservation>(`/reservations/${publicId}`, backendPayload);
            return response;
        } catch (error) {
            console.error(`Error updating reservation ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Cancel reservation
     * @param publicId - Reservation public ID
     * @returns Promise<Reservation>
     */
    cancel: async (publicId: string): Promise<Reservation> => {
        try {
            const response = await api.patch<Reservation>(`/reservations/${publicId}/cancel`);
            return response;
        } catch (error) {
            console.error(`Error cancelling reservation ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Confirm reservation
     * @param publicId - Reservation public ID
     * @returns Promise<Reservation>
     */
    confirm: async (publicId: string): Promise<Reservation> => {
        try {
            const response = await api.patch<Reservation>(`/reservations/${publicId}/confirm`);
            return response;
        } catch (error) {
            console.error(`Error confirming reservation ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Delete reservation
     * @param publicId - Reservation public ID
     * @returns Promise<void>
     */
    delete: async (publicId: string): Promise<void> => {
        try {
            await api.delete(`/reservations/${publicId}`);
        } catch (error) {
            console.error(`Error deleting reservation ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Extract reservation data from PDF
     * @param pdfFile - PDF file to extract data from
     * @returns Promise<ExtractedReservationData>
     */
    extractFromPDF: async (pdfFile: File): Promise<any> => {
        try {
            // Convert PDF file to base64
            const base64PDF = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
                    const base64 = result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(pdfFile);
            });

            // Send base64 PDF to backend
            const response = await api.post<any>('/ia/process-pdf', {
                pdf_base64: base64PDF,
                model: 'gpt-4o',
                max_tokens: 2000,
                temperature: 0.1
            });

            // Return the extracted data
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.error || 'Failed to extract data from PDF');
            }
        } catch (error) {
            console.error('Error extracting reservation data from PDF:', error);
            throw error;
        }
    }
};

export default reservationsService;